import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@testbusters/button';
import PageGray from '../components/General/PageGray';
import ExerciseProgressBar from '../components/CustomExercise/ExerciseProgressBar';
import type { ExerciseStep } from '../components/CustomExercise/ExerciseProgressBar';
import ExerciseStep1Pool from '../components/CustomExercise/ExerciseStep1Pool';
import ExerciseStep2Modality from '../components/CustomExercise/ExerciseStep2Modality';
import ExerciseStep3Subjects from '../components/CustomExercise/ExerciseStep3Subjects';
import ExerciseStep4Questions from '../components/CustomExercise/ExerciseStep4Questions';
import { useGetPools, useGetPoolModes, useGetPoolSubjects, poolKeys } from '../hooks/pools/usePools';
import { useCreateTrainingAttempt } from '../hooks/trainingAttempts/useTrainingAttempts';
import type { PoolMode, ApiSubject } from '../api/pools.api';
import H2 from '../components/Typography/Headings/H2';
import P from '../components/Typography/P';
import { Icon } from '@testbusters/icon';
import TransparentButton from '../components/General/TransparentButton';
import { useAuth } from '@/auth/AuthContext';
import { pushGtmEvent } from '@/lib/gtm';

export const Route = createFileRoute('/esercitazioni/crea')({
  component: RouteComponent,
});

const STEPS: ExerciseStep[] = ['pools', 'modality', 'subjects', 'questions'];

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-6 h-6 border-2 border-background-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const STEP_TITLES: Record<ExerciseStep, string> = {
    pools: t('exercise.steps.poolsTitle'),
    modality: t('exercise.steps.modalityTitle'),
    subjects: t('exercise.steps.subjectsTitle'),
    questions: t('exercise.steps.questionsTitle'),
  };

  const STEP_SUBTITLES: Record<ExerciseStep, string> = {
    pools: t('exercise.steps.poolsSubtitle'),
    modality: '',
    subjects: t('exercise.steps.subjectsSubtitle'),
    questions: '',
  };

  const MODE_LABELS: Record<PoolMode, string> = {
    BY_SUBJECT_AND_TOPIC: t('exercise.modality.subjectsTopics'),
    FAVOURITE: t('exercise.modality.favourites'),
    NEVER_ANSWERED: t('exercise.modality.neverAnswered'),
    MOST_WRONG_ME: t('exercise.modality.worstMine'),
    MOST_WRONG_COMMUNITY: t('exercise.modality.worstUsers'),
  };

  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<ExerciseStep>('pools');
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [selectedMode, setSelectedMode] = useState<PoolMode | ''>('');
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(new Set());
  const [questionCount, setQuestionCount] = useState(20);

  const { mutate: createAttempt, isPending: isCreating } = useCreateTrainingAttempt();

  const handleSelectPool = (poolId: string) => {
    queryClient.invalidateQueries({ queryKey: poolKeys.modes(poolId) });
    setSelectedPoolId(poolId);
  };

  const { pools, isFetching } = useGetPools();
  const { modes, isFetching: isFetchingModes } = useGetPoolModes(selectedPoolId);
  const { subjects, isFetching: isFetchingSubjects } = useGetPoolSubjects(selectedPoolId, selectedMode);

  const currentStepIndex = STEPS.indexOf(currentStep);

  const selectedPool = pools.find((p) => p._id === selectedPoolId);

  const maxQuestions = subjects.reduce((total, subject) =>
    total + subject.topics
      .filter((t) => selectedTopicIds.has(t.topicId))
      .reduce((sum, t) => sum + t.count, 0),
  0);

  const progressSummaries: Partial<Record<ExerciseStep, string>> = {
    ...(selectedPool && currentStepIndex > 0 ? { pools: selectedPool.name } : {}),
    ...(selectedMode && currentStepIndex > 1 ? { modality: MODE_LABELS[selectedMode] } : {}),
  };

  const handleClose = () => navigate({ to: '/esercitazioni' });

  const handleBack = () => {
    if (currentStepIndex > 0) setCurrentStep(STEPS[currentStepIndex - 1]);
    else navigate({ to: '/esercitazioni' });
  };

  // Selections made up to (and including) the given step; later steps are
  // omitted so stale values from going back and forth don't leak in.
  const buildSelectionPayload = (upToIndex: number, subjectIds: string[]) => ({
    pool_id: selectedPoolId,
    pool_name: selectedPool?.name,
    ...(upToIndex >= 1 && { modality: selectedMode }),
    ...(upToIndex >= 2 && {
      subject_ids: subjectIds,
      subjects_count: subjectIds.length,
      topics_count: selectedTopicIds.size,
    }),
    ...(upToIndex >= 3 && { questions_count: questionCount }),
  });

  const handleConfirm = () => {
    const subjectIds = subjects
      .filter((s) => s.topics.some((t) => selectedTopicIds.has(t.topicId)))
      .map((s) => s._id);

    if (currentStep === 'questions') {
      pushGtmEvent({
        event: 'create_custom_exercise',
        user_id: user?.profile.sub,
        ...buildSelectionPayload(currentStepIndex, subjectIds),
      });

      createAttempt(
        {
          poolId: selectedPoolId,
          mode: selectedMode as PoolMode,
          subjectIds,
          topicIds: [...selectedTopicIds],
          answersCount: questionCount,
        },
        {
          onSuccess: (attempt) => {
            // This wizard always produces a pool-based custom exercise.
            pushGtmEvent({
              event: 'start_exercise',
              collection_id: attempt.poolId,
              collection_name: attempt.name,
              attempt_id: attempt._id,
              user_id: user?.profile.sub,
              is_custom_exercise: true,
            });
            navigate({ to: '/esercitazioni/$id', params: { id: attempt._id } });
          },
        },
      );
      return;
    }

    pushGtmEvent({
      event: 'create_exercise_step',
      step: currentStep,
      user_id: user?.profile.sub,
      ...buildSelectionPayload(currentStepIndex, subjectIds),
    });

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      if (currentStep === 'subjects') {
        setQuestionCount(Math.min(20, maxQuestions));
      }
      setCurrentStep(STEPS[nextIndex]);
    }
  };

  const handleTopicToggle = (topicId: string) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      next.has(topicId) ? next.delete(topicId) : next.add(topicId);
      return next;
    });
  };

  const handleSubjectToggle = (subject: ApiSubject) => {
    const allTopicIds = subject.topics.map((t) => t.topicId);
    const allSelected = allTopicIds.every((id) => selectedTopicIds.has(id));
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allTopicIds.forEach((id) => next.delete(id));
      } else {
        allTopicIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const isConfirmDisabled =
    isCreating ||
    (currentStep === 'pools' && !selectedPoolId) ||
    (currentStep === 'modality' && !selectedMode) ||
    (currentStep === 'subjects' && selectedTopicIds.size === 0);

  return (
    <PageGray className="min-h-screen flex flex-col justify-between px-4! py-6! lg:p-11! gap-6">
      <div className="flex justify-center relative mb-24 lg:mb-12">
        <TransparentButton
          onClick={handleClose}
          className="absolute top-0 right-0 lg:right-6 flex items-center gap-1 text-sm transition-colors"
        >
          <span className="first-letter:uppercase">{t('common.close')}</span> <Icon name="close" className="text-2xl" />
        </TransparentButton>

        <div className="w-full flex flex-col gap-9 relative max-w-225">
          <ExerciseProgressBar currentStep={currentStep} summaries={progressSummaries} />

          <div className="flex flex-col gap-2 text-center">
            <H2 className="text-3xl! font-bold text-button-primary-primary-default">
              {STEP_TITLES[currentStep]}
            </H2>
            {STEP_SUBTITLES[currentStep] && (
              <P className="text-sm text-gray-500">{STEP_SUBTITLES[currentStep]}</P>
            )}
          </div>

          <div>
            {currentStep === 'pools' && (
              isFetching ? <Spinner /> : (
                <ExerciseStep1Pool
                  pools={pools}
                  selectedPoolId={selectedPoolId}
                  onSelect={handleSelectPool}
                />
              )
            )}
            {currentStep === 'modality' && (
              isFetchingModes ? <Spinner /> : (
                <ExerciseStep2Modality
                  modes={modes}
                  selectedMode={selectedMode}
                  onSelect={setSelectedMode}
                />
              )
            )}
            {currentStep === 'subjects' && (
              isFetchingSubjects ? <Spinner /> : (
                <ExerciseStep3Subjects
                  subjects={subjects}
                  selectedTopicIds={selectedTopicIds}
                  onTopicToggle={handleTopicToggle}
                  onSubjectToggle={handleSubjectToggle}
                />
              )
            )}
            {currentStep === 'questions' && (
              <ExerciseStep4Questions
                count={questionCount}
                maxCount={maxQuestions}
                onChange={setQuestionCount}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white fixed bottom-0 left-0 right-0 flex justify-between items-center gap-3 px-4 py-4 lg:px-9 lg:py-7 shadow-2xl">
        <Button variant="tertiary" className="flex-1 lg:flex-none lg:min-w-52" onClick={handleBack}>
          {currentStepIndex === 0
            ? (t('common.cancel') ?? 'Annulla')
            : (t('common.back') ?? 'Indietro')}
        </Button>
        <Button
          variant="primary"
          className="flex-1 lg:flex-none lg:min-w-52"
          disabled={isConfirmDisabled}
          onClick={handleConfirm}
        >
          {currentStepIndex === STEPS.length - 1
            ? (t('exercise.createConfirmButton') ?? 'Crea esercitazione')
            : (t('common.continue') ?? 'Continua')}
        </Button>
      </div>
    </PageGray>
  );
}
